import get from 'lodash/get';
import React from 'react';
import styled from 'styled-components'
import PropTypes from 'prop-types';
import classnames from 'classnames';
import ShareGroup from './share-group';
import SocialIcon from './social-icon';
import { includeWhen } from '../react-utils';
import socialWidgetCSS from './social-widget.styles'

/**
 * Standard component for displaying share options on content pages. This is
 * intended for use on pages that target users high in the purchase funnel and
 * existing customers who may share content to their social networks,
 * promoting Glowforge. Deeper in the funnel, emphasis should be placed on
 * a purchase-related call to action instead of social.
 *
 * This component delegates rendering of individual ShareButtons to ShareGroup.
 *
 * @type {{[p: string]: *}} sites - passed on to ShareGroup - ordered list of
 *  social sites
 * @param pageTitle - the title of the page to be shared e.g. `document.title`
 * @param expandDirection - left | right - the side on which the ShareGroup
 *  appears
 * @param url - passed on to ShareButton - the canonical url of the content
 *  being shared
 * @param hashtag - passed on to ShareButton - do not include the # character
 *  e.g. 'glowforge'
 * @param text - passed on to ShareButton - the primary short-text to
 *  pre-populate share content
 * @param quote - passed on to ShareButton - a quote from Glowforge to include
 *  alongside the url
 * @param image - passed on to ShareButton - an image to share (only supported
 *  by pinterest)
 * @param props.size - passed on to SocialIcon - standard sizes small, medium,
 *  or large
 * @param props.appearance - passed on to SocialIcon - available styles square,
 *  round, clear
 */
class ShareWidget extends React.PureComponent {
  static documentTitle() {
    if (typeof window === 'undefined') {
      return null;
    }
    return get(window, ['document', 'title']);
  }

  constructor(props) {
    super(props);
    this.state = { showFlyout: false };
  }

  mouseIn = () => {
    this.setState({ showFlyout: true });
  }

  mouseOut = () => {
    this.setState({ showFlyout: false });
  }

  render() {
    const {
      className,
      pageTitle,
      expandDirection,
      ...childProps
    } = this.props;
    const { showFlyout } = this.state;

    const flyout = includeWhen(showFlyout, () => (
      <div className='flyout'>
        <div className='pageTitle'>{pageTitle}</div>
        <div className='buttons'>
          <ShareGroup size='small' {...childProps} />
        </div>
      </div>
    ));

    const wrapperClasses = classnames(
      ['ShareWidget', expandDirection, className],
      { active: showFlyout },
    );

    const shareIcon = showFlyout ? 'share' : 'shareLight';

    return (
      <div
        className={wrapperClasses}
        onMouseEnter={this.mouseIn}
        onMouseLeave={this.mouseOut}>
        <div className='inlineWrapper'>
          <div className='shareTrigger'>
            <SocialIcon site={shareIcon} appearance='clear' size='fit' />
          </div>
          {flyout}
        </div>
      </div>
    );
  }
}

ShareWidget.propTypes = {
  ...ShareGroup.propTypes,
  className: PropTypes.string,
  pageTitle: PropTypes.string,
  expandDirection: PropTypes.oneOf(['left', 'right']),
}

ShareWidget.defaultProps = {
  className: null,
  expandDirection: 'right',
  pageTitle: ShareWidget.documentTitle() || 'Glowforge',
  // eslint-disable-next-line react/default-props-match-prop-types
  url: undefined,
}

export default styled(ShareWidget)`
${socialWidgetCSS}
`;
